/**
 * /subscriptions/:id — Subscription manage page (OCP-115).
 *
 * Surfaces:
 *   - Status + period start/end + seat count
 *   - For OMS subs: real-time quota widget (server-side proxy)
 *   - "Cancel at period end" button (with confirmation modal — wording
 *     emphasises that customer keeps access until period_end + no
 *     pro-rata refund)
 *   - "Reactivate" button when cancel_at_period_end is true and the
 *     period has not yet lapsed
 *   - "Upgrade plan" link → /products?family=<family>
 *   - "View invoices" link → /billing?subscription=<id>
 *
 * OCP-528 - cancel + reactivate are administrator-only (portal role `owner`
 * or `admin`), because the published AGB V4 (OWS-D23) 12.2 say so. The
 * server enforces it; this page hides the controls and explains why, so a
 * viewer does not stare at a button that always 403s. Hiding is a courtesy,
 * never the enforcement - see `routes/portal/subscriptions.ts`.
 *
 * OCP-531 - the controls are gated on `billing_provider` (any provider link),
 * not on a single provider's id column. The page never names the provider:
 * which company processes the payment is not customer-facing copy.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

import { ApiError, api } from '../lib/api';
import { featuresToBullets, overageLine } from '../lib/product-features';
import { resetOmsSubscriptionCache } from '../lib/use-has-oms-subscription';
import { fmtDate } from '../lib/format-time';
import { fmtNumber } from '../lib/format-money';
import { intlTag } from '../i18n';

interface Subscription {
  id: string;
  product_id: string;
  product_display_name: string;
  product_family: string;
  billing_period: string;
  /** OCP-210: the subscription's product price + features + overage rate so
   * the detail page can render what the customer is paying for. */
  product_price_eur_cents: number;
  product_features: Record<string, unknown>;
  product_overage_config: Record<string, unknown> | null;
  product_marketing_bullets: string[] | null;
  product_description: string | null;
  status: string;
  current_period_start: string | null;
  /** OCP-777 - `ends` = a finite grant (e.g. a subscription code): access ends, nothing renews. */
  renewal?: 'provider' | 'perpetual_free' | 'ends';
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  cancelled_at: string | null;
  seat_count: number;
  quota_remaining_metered: number | null;
  allow_overage: boolean;
  paddle_subscription_id: string | null;
  polar_subscription_id: string | null;
  /**
   * OCP-531 - which provider bills this subscription, or null when none does
   * (free / code-activated plans). The ONLY field the cancel + reactivate
   * controls are drawn from. It used to be `paddle_subscription_id`, which
   * hid the button from every customer billed through the live provider -
   * while the Terms name that exact button. Never rendered as copy: the
   * portal does not tell customers who processes the payment.
   */
  billing_provider: 'paddle' | 'polar' | null;
  created_at: string;
  /**
   * OCP-528 - the CALLER's own portal role, served with the subscription so
   * the cancel control is drawn from the same session the server gate reads.
   */
  role: PortalRole;
}

type PortalRole = 'owner' | 'admin' | 'viewer' | 'billing-only';

/**
 * OCP-528 - mirrors the server gate in `routes/portal/subscriptions.ts` and
 * AGB V4 (OWS-D23) 12.2 ("Die Kündigung kann nur durch den Administrator
 * erfolgen"; administrator = `owner` or `admin`). Keep the two in lock-step; the server one is the enforcement.
 */
function canManageSubscription(role: PortalRole | null | undefined): boolean {
  return role === 'owner' || role === 'admin';
}

interface UsageResponse {
  available: boolean;
  used?: number;
  total?: number | null;
  period_end?: string | null;
}

/** Response shape from /v1/portal/mail/overview (OCP-128). */
interface MailOverviewResponse {
  oms_configured: boolean;
  customer_exists: boolean;
  quota: { used: number; total: number | null; period_end: string | null } | null;
}

/** Badge classes per status; labels are `status.<key>` in the catalogue (OCP-656). */
const STATUS_CLS: Record<string, string> = {
  active: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
  trialing: 'bg-sky-100 text-sky-800',
  past_due: 'bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200',
  paused: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  cancelled: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  expired: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
};

export function SubscriptionDetailPage() {
  const { t } = useTranslation('subscription-detail');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [sub, setSub] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState<'cancel' | 'reactivate' | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!id) return;
    setError(null);
    try {
      const r = await api<Subscription>(`/v1/portal/subscriptions/${id}`, { signal });
      setSub(r);
      // OCP-189: switched from the legacy
      // /v1/portal/subscriptions/:id/usage (Paddle-only via
      // paddle_subscription_id path-arg) to /v1/portal/mail/overview
      // (provider-agnostic customer-level quota from OMS). The legacy
      // route required paddle_subscription_id, leaving free customers
      // + Polar customers stuck in "Loading usage…" forever.
      if (r.product_family === 'oms') {
        try {
          const o = await api<MailOverviewResponse>('/v1/portal/mail/overview', { signal });
          if (o.quota) {
            setUsage({
              available: true,
              used: o.quota.used,
              total: o.quota.total,
              period_end: o.quota.period_end,
            });
          } else {
            setUsage({ available: false });
          }
        } catch {
          // Usage widget is best-effort — never blocks the page.
          setUsage({ available: false });
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (err instanceof ApiError && err.status === 404) {
        setError('not_found');
      } else if (err instanceof ApiError && err.status === 401) {
        navigate('/login?from=' + encodeURIComponent(`/subscriptions/${id}`));
      } else {
        setError('load_failed');
      }
    }
  }, [id, navigate]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const onCancel = useCallback(async () => {
    if (!id) return;
    setActionPending('cancel');
    try {
      const updated = await api<Subscription>(`/v1/portal/subscriptions/${id}/cancel`, {
        method: 'POST',
      });
      setSub(updated);
      setShowCancelModal(false);
      // OCP-222: cancel may have removed the customer's only OMS sub;
      // invalidate so /mail/* gating recomputes against the new state.
      if (updated.product_family === 'oms') {
        resetOmsSubscriptionCache();
      }
    } catch (err) {
      // OCP-528 - the role gate gets its own state so the banner can name
      // the rule. Reachable without a stale UI: a role can be downgraded in
      // another tab between page load and click.
      if (err instanceof ApiError && err.code === 'subscription_cancel_requires_admin') {
        setError('requires_admin');
      } else if (err instanceof ApiError) {
        setError(`cancel_failed_${err.code ?? err.status}`);
      } else setError('cancel_failed');
    } finally {
      setActionPending(null);
    }
  }, [id]);

  const onReactivate = useCallback(async () => {
    if (!id) return;
    setActionPending('reactivate');
    try {
      const updated = await api<Subscription>(
        `/v1/portal/subscriptions/${id}/reactivate`,
        { method: 'POST' },
      );
      setSub(updated);
      // OCP-222: reactivate may have restored the customer's OMS sub;
      // invalidate so /mail/* gating recomputes against the new state.
      if (updated.product_family === 'oms') {
        resetOmsSubscriptionCache();
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'subscription_reactivate_requires_admin') {
        setError('requires_admin');
      } else if (err instanceof ApiError) {
        setError(`reactivate_failed_${err.code ?? err.status}`);
      } else setError('reactivate_failed');
    } finally {
      setActionPending(null);
    }
  }, [id]);

  if (error === 'not_found') {
    return (
      <div className="mx-auto max-w-3xl">
        <h1 className="text-2xl font-semibold tracking-tight">{t('notFoundTitle')}</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {t('notFoundText')}
          <Link to="/subscriptions" className="text-brand-700 underline">
            {t('backToSubscriptions')}
          </Link>
        </p>
      </div>
    );
  }

  if (!sub) {
    return <p className="text-sm text-gray-500">{t('loading')}</p>;
  }

  const badge = {
    label: STATUS_CLS[sub.status] ? t(`status.${sub.status}`) : sub.status,
    cls: STATUS_CLS[sub.status] ?? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  };
  const isCancelled = sub.status === 'cancelled' || sub.status === 'expired';
  // OCP-528 - role first: a viewer never sees either control.
  const canManage = canManageSubscription(sub.role);
  // OCP-531 - a provider link of EITHER kind makes the subscription
  // manageable. Reading one id column is what made the button disappear for
  // customers of the live provider.
  const hasBillingLink = sub.billing_provider !== null;
  const cancellable = !isCancelled && !sub.cancel_at_period_end && hasBillingLink;
  // Reactivate needs the link too - otherwise a pending-cancel row with no
  // provider would show a button that can only 409.
  const reactivatable = !isCancelled && sub.cancel_at_period_end && hasBillingLink;
  const showCancelButton = cancellable && canManage;
  const showReactivateButton = reactivatable && canManage;
  // Explain the missing control rather than silently rendering nothing -
  // a viewer who came here to cancel needs to know who can.
  const showAdminOnlyNote = (cancellable || reactivatable) && !canManage;
  // OCP-531 - the other reason a control can be missing: the plan is not tied
  // to a recurring payment at all (free tier, redeemed code). Say so instead
  // of rendering an unexplained blank, same principle as the admin-only note.
  const showNoBillingLinkNote = !isCancelled && !hasBillingLink;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <Link to="/subscriptions" className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
          {t('back')}
        </Link>
        <div className="mt-2 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{sub.product_display_name}</h1>
            <p className="mt-1 text-sm text-gray-500">
              {sub.product_family} · {sub.billing_period}
              {sub.seat_count > 1 ? t('seats', { count: sub.seat_count }) : ''}
            </p>
          </div>
          <span
            data-testid="sub-status-badge"
            className={`rounded px-2 py-0.5 text-xs font-medium ${badge.cls}`}
          >
            {badge.label}
          </span>
        </div>
      </div>

      {error === 'requires_admin' && (
        <div
          role="alert"
          data-testid="requires-admin-alert"
          className="rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
        >
          {t('requiresAdmin')}
        </div>
      )}

      {error && error !== 'not_found' && error !== 'requires_admin' && (
        <div role="alert" className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {t('actionFailed', { error })}
        </div>
      )}

      {sub.cancel_at_period_end && !isCancelled && (
        <div
          data-testid="cancel-pending-banner"
          className="rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
        >
          {t('cancelPending')}
          <strong>{formatDate(sub.current_period_end)}</strong>
          {t('cancelPendingAfter')}
        </div>
      )}

      <WhatsIncludedCard sub={sub} />

      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t('period')}
        </h2>
        <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-gray-500">{t('periodStart')}</dt>
            <dd className="text-gray-900 dark:text-gray-100">{formatDate(sub.current_period_start)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t('periodEnd')}</dt>
            <dd className="text-gray-900 dark:text-gray-100">{formatDate(sub.current_period_end)}</dd>
          </div>
        </dl>
        {sub.renewal === 'ends' && !isCancelled && (
          <p data-testid="grant-ends-note" className="mt-3 text-xs text-gray-500">
            {t('grantEnds', { date: formatDate(sub.current_period_end) })}
          </p>
        )}
      </section>

      {sub.product_family === 'oms' && (
        <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            {t('mailUsage')}
          </h2>
          <UsageWidget usage={usage} />
        </section>
      )}

      {sub.product_family === 'oms' && <OverageSettingsCard />}

      <section className="flex flex-wrap gap-3">
        <Link
          to={`/products?family=${encodeURIComponent(sub.product_family)}`}
          className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('upgradePlan')}
        </Link>
        <Link
          to={`/billing?subscription=${sub.id}`}
          className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('viewInvoices')}
        </Link>
        {showCancelButton && (
          <button
            type="button"
            data-testid="cancel-subscription-button"
            onClick={() => setShowCancelModal(true)}
            className="ml-auto rounded border border-red-300 dark:border-red-900/50 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-50"
          >
            {/*
              CONTRACT-LOCKED LABEL - do not rename.
              The Terms quote this string verbatim: the administrator clicks
              the button "Abonnement Kündigen" (AGB V4 12.2, OWS-D23), in both
              portal languages (operator 2026-10-17). A customer following the
              Terms literally has to find these words on screen. Changing the
              value means changing the published Terms FIRST, via an OWS ticket
              against the clause - not a copy tweak in the catalogue.
            */}
            {t('cancelSubscription')}
          </button>
        )}
        {showReactivateButton && (
          <button
            type="button"
            onClick={onReactivate}
            disabled={actionPending !== null}
            className="ml-auto rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {actionPending === 'reactivate' ? t('reactivating') : t('reactivate')}
          </button>
        )}
      </section>

      {showNoBillingLinkNote && (
        <p
          data-testid="no-billing-link-note"
          className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 px-3 py-2 text-xs text-gray-600 dark:text-gray-400"
        >
          {t('noBillingLink')}
          <a href="mailto:support@orboto.io" className="text-brand-700 underline">
            support@orboto.io
          </a>
          .
        </p>
      )}

      {showAdminOnlyNote && (
        <p
          data-testid="cancel-admin-only-note"
          className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 px-3 py-2 text-xs text-gray-600 dark:text-gray-400"
        >
          {t('adminOnlyNote', { role: t(`role.${sub.role}`) })}
        </p>
      )}

      {showCancelModal && (
        <CancelConfirmModal
          periodEnd={sub.current_period_end}
          pending={actionPending === 'cancel'}
          onConfirm={onCancel}
          onClose={() => setShowCancelModal(false)}
        />
      )}
    </div>
  );
}

/**
 * OCP-210: "What's included" card showing the subscription's price +
 * product description + feature list + overage rate. Pulled from the
 * subscription detail response (no extra round-trip); operator-curated
 * `marketing_bullets` win when present, otherwise we render the
 * features object via the shared helper.
 */
function WhatsIncludedCard({ sub }: { sub: Subscription }) {
  const { t } = useTranslation('subscription-detail');
  const bullets =
    sub.product_marketing_bullets && sub.product_marketing_bullets.length > 0
      ? sub.product_marketing_bullets
      : featuresToBullets(sub.product_features);
  const overage = overageLine(sub.product_overage_config);
  const hasContent =
    sub.product_description !== null || bullets.length > 0 || overage !== null;
  if (!hasContent) return null;
  return (
    <section
      data-testid="whats-included-card"
      className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t('whatsIncluded')}
        </h2>
        {sub.product_price_eur_cents > 0 && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <PriceLabel cents={sub.product_price_eur_cents} period={sub.billing_period} />
          </p>
        )}
      </div>
      {sub.product_description && (
        <div className="prose prose-sm mt-3 max-w-none text-gray-700 dark:text-gray-300">
          <ReactMarkdown>{sub.product_description}</ReactMarkdown>
        </div>
      )}
      {bullets.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm text-gray-700 dark:text-gray-300">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <span aria-hidden="true" className="text-brand-500">
                ·
              </span>
              <span className="prose prose-sm max-w-none [&_p]:m-0">
                <ReactMarkdown>{b}</ReactMarkdown>
              </span>
            </li>
          ))}
        </ul>
      )}
      {overage && (
        <p
          data-testid="overage-rate-line"
          className="mt-3 rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
        >
          <strong>{t('beyondQuota')}</strong> {overage}.
        </p>
      )}
    </section>
  );
}

function PriceLabel({ cents, period }: { cents: number; period: string }) {
  const { t } = useTranslation('subscription-detail');
  const eur = new Intl.NumberFormat(intlTag(), {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
  const periodSuffix =
    period === 'monthly'
      ? t('perMonth')
      : period === 'yearly'
        ? t('perYear')
        : period === 'one-time'
          ? t('oneTime')
          : '';
  return (
    <>
      <span className="font-medium text-gray-900 dark:text-gray-100">{eur}</span>
      {periodSuffix && (
        <span className="text-xs text-gray-500">{periodSuffix}</span>
      )}
    </>
  );
}

function UsageWidget({ usage }: { usage: UsageResponse | null }) {
  const { t } = useTranslation('subscription-detail');
  if (!usage) return <p className="mt-2 text-sm text-gray-500">{t('usageLoading')}</p>;
  if (!usage.available) {
    return (
      <p className="mt-2 text-sm text-gray-500" data-testid="usage-unavailable">
        {t('usageUnavailable')}
      </p>
    );
  }
  const used = usage.used ?? 0;
  const total = usage.total ?? null;
  const pct = total && total > 0 ? Math.min(100, Math.round((used / total) * 100)) : null;
  return (
    <div className="mt-3" data-testid="usage-widget">
      <p className="text-sm text-gray-700 dark:text-gray-300">
        <strong>{fmtNumber(used)}</strong>
        {total != null ? ` / ${fmtNumber(total)}` : ''}
        {t('usageThisPeriod')}
      </p>
      {pct != null && (
        <div className="mt-2 h-2 w-full overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
          <div
            data-testid="usage-bar"
            className="h-full bg-brand-500 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      {usage.period_end && (
        <p className="mt-1 text-xs text-gray-500">{t('usageResets', { date: formatDate(usage.period_end) })}</p>
      )}
    </div>
  );
}

function CancelConfirmModal({
  periodEnd,
  pending,
  onConfirm,
  onClose,
}: {
  periodEnd: string | null;
  pending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('subscription-detail');
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-modal-title"
      data-testid="cancel-confirm-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 p-5 shadow-lg">
        <h2 id="cancel-modal-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {t('cancelModal.title')}
        </h2>
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
          {t('cancelModal.textBefore')}
          <strong>{formatDate(periodEnd)}</strong>
          {t('cancelModal.textAfter')}
        </p>
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            {t('cancelModal.keep')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="rounded bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {/* Same contract-locked wording as the page button - see there. */}
            {pending ? t('cancelModal.cancelling') : t('cancelSubscription')}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string | null): string {
  return fmtDate(iso, '—');
}

/**
 * OCP-162 — Overage settings card. Toggle hard_gate | use_credits +
 * monthly euro cap + shows current-month accrued spend so the customer
 * knows where they stand.
 */
interface OverageState {
  overage_mode: 'hard_gate' | 'use_credits';
  monthly_cap_eur_cents: number;
  current_month_spend_eur_cents: number;
  credit_balance: number;
  would_block: boolean;
}

function OverageSettingsCard() {
  const { t } = useTranslation('subscription-detail');
  const [state, setState] = useState<OverageState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [draftMode, setDraftMode] = useState<OverageState['overage_mode']>('hard_gate');
  const [draftCapEur, setDraftCapEur] = useState('0');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      try {
        const r = await api<OverageState>('/v1/portal/me/mail-overage-settings', {
          signal: ac.signal,
        });
        setState(r);
        setDraftMode(r.overage_mode);
        setDraftCapEur((r.monthly_cap_eur_cents / 100).toFixed(2));
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiError) setLoadError(`HTTP ${err.status}`);
        else setLoadError(t('overage.loadFailedShort'));
      }
    })();
    return () => ac.abort();
  }, [t]);

  async function save() {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const cents = Math.max(0, Math.round(Number(draftCapEur) * 100));
      const r = await api<OverageState>('/v1/portal/me/mail-overage-settings', {
        method: 'PATCH',
        body: { overage_mode: draftMode, monthly_cap_eur_cents: cents },
      });
      setState(r);
    } catch (err) {
      if (err instanceof ApiError) setSaveError(t('overage.saveFailedHttp', { status: err.status }));
      else setSaveError(t('overage.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  if (loadError) {
    return (
      <section className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 p-5">
        <h2 className="text-sm font-semibold text-red-900 dark:text-red-200">{t('overage.title')}</h2>
        <p className="mt-1 text-xs text-red-700 dark:text-red-300">{t('overage.loadFailed', { error: loadError })}</p>
      </section>
    );
  }
  if (!state) {
    return (
      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t('overage.title')}
        </h2>
        <p className="mt-2 text-sm text-gray-500">{t('overage.loading')}</p>
      </section>
    );
  }

  const dirty =
    draftMode !== state.overage_mode ||
    Math.round(Number(draftCapEur) * 100) !== state.monthly_cap_eur_cents;

  return (
    <section
      data-testid="overage-settings-card"
      className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
        {t('overage.title')}
      </h2>
      <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
        {t('overage.intro')}
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat label={t('overage.creditBalance')} value={fmtNumber(state.credit_balance)} tone="neutral" />
        <Stat
          label={t('overage.monthSpend')}
          value={`€${(state.current_month_spend_eur_cents / 100).toFixed(2)}`}
          tone="neutral"
        />
        <Stat
          label={t('overage.nextSend')}
          value={state.would_block ? t('overage.blocked') : t('overage.allowed')}
          tone={state.would_block ? 'red' : 'emerald'}
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-gray-600 dark:text-gray-400">{t('overage.mode')}</span>
          <select
            value={draftMode}
            onChange={(e) => setDraftMode(e.target.value as OverageState['overage_mode'])}
            className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          >
            <option value="hard_gate">{t('overage.hardGate')}</option>
            <option value="use_credits">{t('overage.useCredits')}</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-gray-600 dark:text-gray-400">{t('overage.monthlyCap')}</span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={draftCapEur}
            onChange={(e) => setDraftCapEur(e.target.value)}
            disabled={draftMode === 'hard_gate'}
            className="w-40 rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm tabular-nums disabled:bg-gray-100 dark:disabled:bg-gray-800/60"
          />
        </label>
        <button
          type="button"
          onClick={() => void save()}
          disabled={!dirty || saving}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          {saving ? t('overage.saving') : t('overage.save')}
        </button>
      </div>
      {draftMode === 'use_credits' && Number(draftCapEur) <= 0 && (
        <p className="mt-2 text-xs text-amber-700">
          {t('overage.capZero')}
        </p>
      )}
      {saveError && (
        <p className="mt-2 text-xs text-red-700 dark:text-red-300">{saveError}</p>
      )}
    </section>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'neutral' | 'red' | 'emerald';
}) {
  const cls =
    tone === 'red'
      ? 'text-red-700 dark:text-red-300'
      : tone === 'emerald'
        ? 'text-emerald-700 dark:text-emerald-300'
        : 'text-gray-900 dark:text-gray-100';
  return (
    <div className="rounded border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 py-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`mt-0.5 text-lg font-semibold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
